import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  CheckCircle, 
  Clock, 
  Euro, 
  Headphones, 
  Settings, 
  Shield,
  Zap,
  Users,
  Phone,
  ArrowRight,
  Star,
  Wrench,
  Monitor,
  Server
} from "lucide-react";

const ServiceModels = () => {
  const servicePackages = [
    {
      title: "IT-Wartungspaket Basis",
      description: "Grundlegende IT-Wartung für kleine Unternehmen",
      price: "ab 150€/Monat - 2 Stunden inkl.", 
      features: [
        "Monatliche System-Checks",
        "Software-Updates",
        "Grundlegendes Monitoring",
        "E-Mail Support",
        "Remote-Wartung",
        "2 Stunden Support inklusive"
      ],
      icon: Settings,
      recommended: false
    },
    {
      title: "IT-Wartungspaket Professional", 
      description: "Umfassende IT-Betreuung für mittlere Unternehmen",
      price: "ab 299€/Monat - 4 Stunden inkl.",
      features: [
        "Kontinuierliches Monitoring",
        "Proaktive Wartung",
        "Prioritäts-Support",
        "Backup-Management",
        "Vor-Ort Service inklusive",
        "Monatliche Reports",
        "4 Stunden Support inklusive"
      ],
      icon: Server,
      recommended: true
    },
    {
      title: "IT-Service on Demand",
      description: "Flexible IT-Services nach Bedarf", 
      price: "ab 95€/Stunde",
      features: [
        "Abrechnung nach Aufwand",
        "Keine Vertragsbindung",
        "Flexible Einsatzzeiten",
        "Vor-Ort und Remote",
        "Transparente Abrechnung"
      ],
      icon: Wrench,
      recommended: false
    }
  ];

  const comparisonFeatures = [
    "Professioneller IT-Support",
    "Remote Administration", 
    "Regelmäßige Wartung",
    "Software-Updates",
    "Hardware-Monitoring", 
    "Backup-Services",
    "Security-Updates",
    "Performance-Optimierung",
    "Dokumentation",
    "Vor-Ort Service"
  ];

  return (
    <div className="min-h-screen py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <Badge className="bg-blue-100 text-blue-800">Service-Pakete</Badge>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900">
            IT-Service-Pakete für Ihr Unternehmen
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Maßgeschneiderte IT-Services - von flexiblen Stundenpaketen bis zu 
            umfassenden Wartungsverträgen für Ihre IT-Infrastruktur.
          </p>
        </div>

        {/* Service Packages */}
        <section className="mb-20">
          <div className="grid lg:grid-cols-3 gap-8">
            {servicePackages.map((pkg, index) => (
              <Card 
                key={index}
                className={`relative ${
                  pkg.recommended 
                    ? 'border-blue-500 shadow-lg ring-2 ring-blue-200 transform scale-105' 
                    : 'border-gray-200'
                }`}
              >
                {pkg.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-blue-600 text-white px-4 py-1">
                      <Star className="mr-1 h-3 w-3" />
                      Beliebt
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="text-center">
                  <div className="mb-4">
                    <pkg.icon className="h-12 w-12 text-blue-600 mx-auto" />
                  </div>
                  <CardTitle className="text-xl">{pkg.title}</CardTitle>
                  <CardDescription className="text-sm">{pkg.description}</CardDescription>
                  <div className="text-2xl font-bold text-blue-600 mt-4">{pkg.price}</div>
                </CardHeader>
                
                <CardContent>
                  <ul className="space-y-2 mb-8">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    className={`w-full ${
                      pkg.recommended 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-gray-600 hover:bg-gray-700'
                    }`}
                    asChild
                  >
                    <Link to="/contact">
                      Angebot anfragen
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Why Choose Our Service Packages */}
        <section className="mb-20">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              Warum unsere Service-Pakete?
            </h2>
            <p className="text-lg text-gray-600">
              Professionelle IT-Betreuung nach Ihren Anforderungen
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="text-center p-6">
              <div className="mb-4">
                <Clock className="h-12 w-12 text-blue-600 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Schnelle Reaktionszeiten</h3>
              <p className="text-gray-600">
                Professioneller Support bei kritischen IT-Problemen innerhalb von 2-4 Stunden
              </p>
            </Card>

            <Card className="text-center p-6">
              <div className="mb-4">
                <Shield className="h-12 w-12 text-green-600 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Proaktive Wartung</h3>
              <p className="text-gray-600">
                Regelmäßige System-Checks und Updates verhindern Ausfälle bevor sie auftreten
              </p>
            </Card>

            <Card className="text-center p-6">
              <div className="mb-4">
                <Euro className="h-12 w-12 text-purple-600 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Transparente Kosten</h3>
              <p className="text-gray-600">
                Klare Preisstrukturen ohne versteckte Kosten - Sie wissen immer was Sie zahlen
              </p>
            </Card>
          </div>
        </section>

        {/* Service Comparison */}
        <section className="mb-20 bg-gray-50 p-8 rounded-2xl">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              Welches Paket ist das Richtige?
            </h2>
            <p className="text-lg text-gray-600">
              Finden Sie das passende Service-Paket für Ihr Unternehmen
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center p-6">
              <div className="mb-4">
                <Wrench className="h-12 w-12 text-blue-600 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold mb-2">On-Demand</h3>
              <p className="text-gray-600 mb-4">
                Perfekt für kleinere Unternehmen mit gelegentlichem IT-Bedarf
              </p>
              <ul className="text-sm space-y-1">
                <li>• Keine Vertragsbindung</li>
                <li>• Flexible Abrechnung</li>
                <li>• Für 1-10 Arbeitsplätze</li>
              </ul>
            </Card>

            <Card className="text-center p-6 border-blue-500 ring-2 ring-blue-200">
              <div className="mb-4">
                <Server className="h-12 w-12 text-blue-600 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Professional</h3>
              <Badge className="mb-2 bg-blue-600 text-white">Empfohlen</Badge>
              <p className="text-gray-600 mb-4">
                Ideal für mittelständische Unternehmen mit regelmäßigem IT-Bedarf
              </p>
              <ul className="text-sm space-y-1">
                <li>• Proaktives Monitoring</li>
                <li>• Planbare Kosten</li>
                <li>• Für 10-50 Arbeitsplätze</li>
              </ul>
            </Card>

            <Card className="text-center p-6">
              <div className="mb-4">
                <Settings className="h-12 w-12 text-blue-600 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Basis</h3>
              <p className="text-gray-600 mb-4">
                Grundlegende IT-Wartung für kostenbewusste Unternehmen
              </p>
              <ul className="text-sm space-y-1">
                <li>• Grundlegende Wartung</li>
                <li>• Günstige Einstiegslösung</li>
                <li>• Für 3-15 Arbeitsplätze</li>
              </ul>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center space-y-8 bg-blue-600 p-12 rounded-2xl">
          <h2 className="text-3xl font-bold text-white">
            Bereit für professionelle IT-Betreuung?
          </h2>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Lassen Sie uns gemeinsam das passende Service-Paket für Ihr 
            Unternehmen finden. Kontaktieren Sie uns für eine unverbindliche Beratung.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" asChild>
              <Link to="/contact">
                Kostenlose Beratung anfragen
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-blue-600" 
              asChild
            >
              <Link to="/services">
                Alle Services ansehen
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ServiceModels;